import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { WaButton, CallButton, QuoteButton, fadeSlide } from "./Buttons";
import { HERO_IMG } from "../lib/site";

const line = (children, i) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.05, delay: 0.18 + i * 0.13, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

const CHIPS = ["Cantiere pulito", "Materiali di qualità", "Garanzia scritta"];

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const yGlow = useTransform(scrollYProgress, [0, 1], [0, -70]);

    return (
        <section ref={ref} id="top" className="relative min-h-screen grid-bg overflow-hidden" data-testid="hero-section">
            <motion.div
                style={{ y: yGlow }}
                className="absolute -top-40 -right-40 w-[720px] h-[720px] rounded-full pointer-events-none"
            >
                <div className="absolute inset-0 rounded-full bg-azure/25 blur-[140px]" />
                <div className="absolute inset-24 rounded-full bg-aqua/20 blur-[110px]" />
            </motion.div>

            <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-28 md:pt-36 pb-16 grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-16 items-center">
                <div>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.05 }}
                        className="eyebrow mb-6 flex items-center gap-3"
                    >
                        <span className="w-2 h-2 bg-wa rounded-full pulse-dot" />
                        Impermeabilizzazioni · Coperture · Resine
                    </motion.p>

                    <h1 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-[5.6rem]">
                        {line(<span className="text-cloud">L&rsquo;acqua</span>, 0)}
                        {line(
                            <span className="text-outline">resta</span>,
                            1
                        )}
                        {line(
                            <span className="text-ambra">fuori.</span>,
                            2
                        )}
                    </h1>

                    <motion.p
                        variants={fadeSlide}
                        custom={4}
                        initial="hidden"
                        animate="show"
                        className="mt-7 max-w-md text-base sm:text-lg text-ash leading-relaxed"
                    >
                        Terrazzi e lastrici trattati a regola d&rsquo;arte: guaina bituminosa,
                        resine e pitture elastiche. Diagnosi onesta, materiali giusti, cantieri
                        lasciati puliti.
                    </motion.p>

                    <motion.div
                        variants={fadeSlide}
                        custom={5}
                        initial="hidden"
                        animate="show"
                        className="mt-9 flex flex-col sm:flex-row sm:flex-wrap gap-3"
                    >
                        <WaButton data-testid="hero-cta-whatsapp" />
                        <CallButton />
                        <QuoteButton />
                    </motion.div>

                    <motion.ul
                        variants={fadeSlide}
                        custom={6}
                        initial="hidden"
                        animate="show"
                        className="mt-10 flex flex-wrap gap-x-6 gap-y-2"
                        data-testid="hero-chips"
                    >
                        {CHIPS.map((c) => (
                            <li key={c} className="font-mono2 text-[11px] uppercase tracking-[0.18em] text-ash flex items-center gap-2">
                                <span className="w-1 h-1 bg-ambra" />
                                {c}
                            </li>
                        ))}
                    </motion.ul>
                </div>

                <motion.div
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 1.1, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
                    style={{ y: yImg }}
                    className="relative hidden lg:block"
                >
                    <div className="spotlight frame-clipped border border-white/10" data-testid="hero-image-frame">
                        <img
                            src={HERO_IMG}
                            alt="Operatori al lavoro su un tetto con guaina bituminosa"
                            className="w-full h-[68vh] object-cover img-treated"
                            loading="eager"
                        />
                    </div>
                    <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between bg-bitume/70 backdrop-blur-md border border-white/10 px-5 py-4 frame-clipped-sm" data-testid="hero-image-caption">
                        <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-aqua">
                            In cantiere
                        </span>
                        <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-ash">
                            Guaina bituminosa a fiamma
                        </span>
                    </div>
                </motion.div>
            </div>

            <motion.a
                href="#servizi"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 0.8 }}
                className="absolute bottom-6 left-5 md:left-8 hidden sm:flex items-center gap-3 font-mono2 text-[10px] uppercase tracking-[0.25em] text-ash hover:text-cloud transition-colors"
                data-testid="hero-scroll-hint"
            >
                <ArrowDown className="w-4 h-4 animate-bounce" />
                Scopri i servizi
            </motion.a>
        </section>
    );
}
